import React, { useEffect, useMemo, useRef, useState } from "react";
import api, { formatApiError } from "../../lib/api";
import { Window, Dialog, EmptyState, Loading, StatCard } from "../../components/VB";
import { toast } from "sonner";
import { Globe, DollarSign, CheckCircle2, AlertCircle, Calendar, ExternalLink, Receipt, Eye, Wallet, Copy, Bitcoin, Landmark, Twitter, Star, MessageSquareWarning, HandCoins, Search, ClipboardCheck, QrCode, EyeOff, ChevronDown } from "lucide-react";
import { QRCodeCanvas } from "qrcode.react";

const usd = (n) => "$ " + (parseFloat(n || 0)).toLocaleString("en-US", { maximumFractionDigits: 2, minimumFractionDigits: 2 });

// Type Web "Proper" ditandai Merah (bukan biru seperti Standart/lainnya) — sama persis aturan
// yang dipakai admin ProjectPayment.jsx.
const isProperType = (t) => (t || "").trim().toLowerCase() === "proper";

// Rating 1-5 bintang. Untuk akun Group Client: bisa diklik untuk memberi rating (menentukan
// potongan harga — tiap bintang di bawah 5 = -10%, 1★ = bayar 60%). Untuk mode lain: read-only.
function RatingStars({ value, editable, onRate, size = 14 }) {
  const [hover, setHover] = useState(0);
  const shown = hover || parseInt(value || 0);
  return (
    <span
      style={{ display: "inline-flex", gap: 1, whiteSpace: "nowrap", verticalAlign: "middle" }}
      title={editable ? "Klik bintang untuk memberi rating project ini" : (value ? `Rating ${value} bintang` : "Belum dirating")}
      onMouseLeave={editable ? () => setHover(0) : undefined}
    >
      {[1, 2, 3, 4, 5].map((s) => (
        <Star
          key={s}
          style={{
            width: size, height: size,
            cursor: editable ? "pointer" : "default",
            color: s <= shown ? "#f5a623" : "#ccc",
            fill: s <= shown ? "#f5a623" : "none",
          }}
          onMouseEnter={editable ? () => setHover(s) : undefined}
          onClick={editable ? () => onRate(s) : undefined}
        />
      ))}
    </span>
  );
}

const twitterHref = (v) => {
  const s = (v || "").trim();
  if (!s) return "";
  if (/^https?:\/\//i.test(s)) return s;
  return `https://x.com/${s.replace(/^@/, "")}`;
};

const TYPE_ORDER_LABEL = { WEBSITE: "Website", MATERIAL_ONLY: "Material Only", REBRAND: "Rebrand" };

// Build list of periode options (tgl 15 & 30 each month, last 13 months — descending)
function generatePeriodeOptions() {
  const out = [];
  const today = new Date();
  for (let m = 0; m < 13; m++) {
    const d = new Date(today.getFullYear(), today.getMonth() - m, 1);
    const y = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, "0");
    out.push(`${y}-${month}-30`);
    out.push(`${y}-${month}-15`);
  }
  return out;
}

const monthLabel = ["Jan", "Feb", "Mar", "Apr", "Mei", "Jun", "Jul", "Agu", "Sep", "Okt", "Nov", "Des"];
function formatPeriode(p) {
  if (!p) return "Semua periode";
  const [y, m, d] = p.split("-");
  return `${parseInt(d)} ${monthLabel[parseInt(m) - 1]} ${y}`;
}

// Multi-select periode dengan checkbox. `value` = array periode (YYYY-MM-DD); array kosong =
// Semua Periode. Panel memakai position: fixed (dihitung dari tombol) karena .vb-window punya
// overflow: hidden yang akan memotong dropdown absolut.
function PeriodeMultiSelect({ options, value, onChange, disabled, title }) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState(null);
  const btnRef = useRef(null);
  const panelRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e) => {
      if (panelRef.current?.contains(e.target) || btnRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    const onScroll = (e) => { if (!panelRef.current?.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDown);
    window.addEventListener("scroll", onScroll, true);
    window.addEventListener("resize", onScroll);
    return () => {
      document.removeEventListener("mousedown", onDown);
      window.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("resize", onScroll);
    };
  }, [open]);

  const toggleOpen = () => {
    if (disabled) return;
    if (!open && btnRef.current) {
      const r = btnRef.current.getBoundingClientRect();
      setPos({ top: r.bottom + 2, left: r.left, width: Math.max(r.width, 220) });
    }
    setOpen((v) => !v);
  };

  const selected = new Set(value);
  const toggle = (p) => {
    const next = new Set(selected);
    if (next.has(p)) next.delete(p); else next.add(p);
    onChange(options.filter((o) => next.has(o)));
  };

  const label = value.length === 0
    ? "Semua Periode"
    : value.length === 1 ? formatPeriode(value[0]) : `${value.length} periode dipilih`;

  const rowStyle = { display: "flex", alignItems: "center", gap: 8, padding: "4px 10px", fontSize: 13, cursor: "pointer", userSelect: "none" };

  return (
    <>
      <button
        type="button"
        ref={btnRef}
        className="vb-select"
        style={{ width: 220, textAlign: "left", display: "flex", alignItems: "center", justifyContent: "space-between", opacity: disabled ? 0.6 : 1, cursor: disabled ? "not-allowed" : "pointer" }}
        onClick={toggleOpen}
        disabled={disabled}
        title={title}
        data-testid="cli-filter-periode"
      >
        <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{label}</span>
        <ChevronDown className="w-3 h-3" style={{ flexShrink: 0 }} />
      </button>
      {open && pos && (
        <div
          ref={panelRef}
          style={{ position: "fixed", top: pos.top, left: pos.left, width: pos.width, maxHeight: 340, overflowY: "auto", background: "white", border: "1px solid #828790", boxShadow: "0 4px 10px rgba(0,0,0,0.2)", zIndex: 1000, fontWeight: "normal", textShadow: "none" }}
          data-testid="cli-filter-periode-panel"
        >
          <label style={{ ...rowStyle, borderBottom: "1px solid #ddd", fontWeight: 600 }}>
            <input type="checkbox" checked={value.length === 0} onChange={() => onChange([])} />
            Semua Periode
          </label>
          {options.map((p) => (
            <label key={p} style={{ ...rowStyle, background: selected.has(p) ? "#e4effd" : undefined }}>
              <input type="checkbox" checked={selected.has(p)} onChange={() => toggle(p)} data-testid={`cli-periode-check-${p}`} />
              {formatPeriode(p)}
            </label>
          ))}
        </div>
      )}
    </>
  );
}

// `forceScope` ("client"|"group") dipakai route "/client/group" (menu "Project Group") untuk
// akun yang tertaut Client individu DAN Group Client sekaligus — supaya scope-nya eksplisit,
// tidak bergantung pada prioritas default backend (_client_project_filter). Route index
// ("/client", menu "Project Per Client"/"Dashboard & Project") sengaja TIDAK mengirim
// forceScope — default backend sudah memprioritaskan client_id kalau ada, jatuh ke group_id
// kalau tidak, jadi tetap benar untuk akun yang cuma tertaut salah satu maupun yang tertaut
// keduanya.
export default function ClientDashboard({ forceScope } = {}) {
  const [me, setMe] = useState(null);
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState(null);
  const [paymentInfo, setPaymentInfo] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterPeriode, setFilterPeriode] = useState([]);  // [] = Semua Periode, bisa >1
  const [filterPayment, setFilterPayment] = useState("");  // "" / Lunas / Sebagian / Pending
  const [filterListing, setFilterListing] = useState("");  // "" / Sudah Listing / Belum Listing
  const [filterClient, setFilterClient] = useState("");    // "" / client_id — hanya relevan untuk mode group
  const [searchTerm, setSearchTerm] = useState("");        // cari by nama project / domain — MENGABAIKAN filterPeriode
  const [detail, setDetail] = useState(null);
  const [periodePage, setPeriodePage] = useState(1);
  const PERIODE_PAGE_SIZE = 10;

  // Pengajuan Revisi (Group Client) — modal textarea.
  const [revisiTarget, setRevisiTarget] = useState(null);
  const [revisiNote, setRevisiNote] = useState("");
  // Beri Tip tambahan USD (Group Client) — modal input nominal.
  const [tipTarget, setTipTarget] = useState(null);
  const [tipAmount, setTipAmount] = useState("");
  // Dialog "Alamat Pembayaran" — quick-access ke daftar rekening/wallet yang sama persis dengan
  // yang sudah ditampilkan inline di bawah (Window "Informasi Pembayaran"), dibuka dari tombol
  // di card paling atas supaya tidak perlu scroll.
  const [showPaymentDialog, setShowPaymentDialog] = useState(false);
  // Card "Rekap Pembayaran per Periode" disembunyikan secara default — dibuka lewat tombol
  // View/Hide di card paling atas (sebelah "Alamat Pembayaran").
  const [showRekap, setShowRekap] = useState(false);

  const periodeOptions = useMemo(() => generatePeriodeOptions(), []);
  // Tampilan awal default ke periode PALING BARU (bukan "Semua Periode") — cuma diterapkan
  // SEKALI per mount halaman, supaya tidak menimpa balik pilihan periode manual user tiap kali
  // `load()` dipanggil ulang (mis. setelah refresh summary dari aksi rating/tip/mark-listed).
  const didSetDefaultPeriodeRef = useRef(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = forceScope ? { scope: forceScope } : undefined;
      const [m, p, s, pi] = await Promise.all([
        api.get("/client/me", { params }),
        api.get("/client/projects", { params }),
        api.get("/client/summary", { params }),
        api.get("/client/payment-info"),
      ]);
      setMe(m.data);
      setItems(p.data);
      setSummary(s.data);
      setPaymentInfo(pi.data);
      if (!didSetDefaultPeriodeRef.current) {
        didSetDefaultPeriodeRef.current = true;
        const latest = (p.data || []).reduce(
          (max, it) => (it.due_date && (!max || it.due_date > max) ? it.due_date : max), ""
        );
        if (latest) setFilterPeriode([latest]);
      }
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    setLoading(false);
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [forceScope]);

  // Group Client memberi / mengubah rating sebuah project (menentukan potongan harga).
  const setRating = async (id, star) => {
    try {
      const res = await api.patch(`/client/projects/${id}/rating`, { rating: star });
      const r = { client_rating: res.data.client_rating, pay_percent: res.data.pay_percent, amount_due: res.data.amount_due };
      setItems((prev) => prev.map((x) => (x.id === id ? { ...x, ...r } : x)));
      setDetail((d) => (d && d.id === id ? { ...d, ...r } : d));
      toast.success(`Rating ${star} bintang tersimpan — tagihan project ini jadi ${res.data.pay_percent}% (${usd(res.data.amount_due)})`);
      try { const s = await api.get("/client/summary"); setSummary(s.data); } catch { /* summary refresh best-effort */ }
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  // Kirim pengajuan revisi — statusnya langsung "Pending Rev" (cell abu-abu) sampai admin setuju.
  const submitRevisi = async () => {
    if (!revisiTarget) return;
    const note = (revisiNote || "").trim();
    if (!note) { toast.error("Catatan revisi tidak boleh kosong"); return; }
    try {
      const res = await api.post(`/client/projects/${revisiTarget.id}/revisi`, { note });
      const r = { revision_pending: res.data.revision_pending, revision_note: res.data.revision_note, revision_requested_at: res.data.revision_requested_at };
      setItems((prev) => prev.map((x) => (x.id === revisiTarget.id ? { ...x, ...r } : x)));
      setDetail((d) => (d && d.id === revisiTarget.id ? { ...d, ...r } : d));
      toast.success("Permintaan revisi terkirim — menunggu review admin");
      setRevisiTarget(null); setRevisiNote("");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  // Beri tip tambahan (USD) — ditambahkan penuh ke subtotal/amount_due project (tidak ikut
  // dipotong rating).
  const submitTip = async () => {
    if (!tipTarget) return;
    const amount = parseFloat(tipAmount || 0);
    if (!(amount > 0)) { toast.error("Nominal tip harus lebih dari $0"); return; }
    try {
      const res = await api.post(`/client/projects/${tipTarget.id}/tip`, { amount });
      const r = { tip_amount: res.data.tip_amount, amount_due: res.data.amount_due, total_payment: res.data.total_payment };
      setItems((prev) => prev.map((x) => (x.id === tipTarget.id ? { ...x, ...r } : x)));
      setDetail((d) => (d && d.id === tipTarget.id ? { ...d, ...r } : d));
      toast.success(`Tip ${usd(amount)} tersimpan — total tagihan project ini jadi ${usd(res.data.amount_due)}`);
      try { const s = await api.get("/client/summary"); setSummary(s.data); } catch { /* best-effort */ }
      setTipTarget(null); setTipAmount("");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  // Kepala Group menandai project "Belum Listing" jadi selesai TANPA memilih Petugas Listing —
  // backend otomatis mengisi Petugas Listing "Only Farming" dan memaksa biaya listing jadi $0
  // (lihat client_mark_listed di server.py). Satu arah — hanya untuk project yang belum listing.
  const markListed = async (it) => {
    if (!window.confirm(`Tandai "${it.nama_project}" sebagai Sudah Listing?\n\nPetugas Listing akan otomatis diisi "Only Farming" dan TIDAK ADA biaya listing yang dibebankan untuk project ini.`)) return;
    try {
      const res = await api.post(`/client/projects/${it.id}/mark-listed`);
      const r = {
        listing_id: res.data.listing_id, listing_nama: res.data.listing_nama,
        listing_status: res.data.listing_status, listing_marked_by_group: res.data.listing_marked_by_group,
        price_listing: res.data.price_listing, total_payment: res.data.total_payment,
        amount_due: res.data.amount_due, payment_status: res.data.payment_status,
      };
      setItems((prev) => prev.map((x) => (x.id === it.id ? { ...x, ...r } : x)));
      setDetail((d) => (d && d.id === it.id ? { ...d, ...r } : d));
      toast.success(`"${it.nama_project}" ditandai Sudah Listing (Petugas: Only Farming, tanpa biaya listing)`);
      try { const s = await api.get("/client/summary", forceScope ? { params: { scope: forceScope } } : undefined); setSummary(s.data); } catch { /* summary refresh best-effort */ }
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  // Daftar client unik (untuk filter "by Client" pada mode Group Client)
  const clientOptions = useMemo(() => {
    const seen = new Map();
    for (const it of items) {
      if (it.client_id && !seen.has(it.client_id)) seen.set(it.client_id, it.client_nama || it.client_id);
    }
    return [...seen.entries()].map(([id, nama]) => ({ id, nama })).sort((a, b) => a.nama.localeCompare(b.nama));
  }, [items]);

  // Filter chain: client → (search ATAU periode) → payment status → listing status.
  // Pencarian (nama project / domain) SENGAJA mengabaikan filterPeriode — mencari di SELURUH
  // project yang sudah dimuat (semua periode di group ini, `items` memang tidak pernah
  // dibatasi periode sejak awal dimuat), sesuai permintaan literal ("pencarian tidak terbatas
  // pada periode yang dipilih tapi all data di group dia disemua periode"). Filter lain
  // (client/payment/listing) tetap berlaku dikombinasikan dengan pencarian.
  const searchActive = searchTerm.trim().length > 0;
  // Semua filter KECUALI client — dipakai untuk menghitung jumlah web per client di dropdown
  // "Semua Client" (jumlahnya mengikuti periode/search/listing/payment yang sedang aktif).
  const itemsExceptClient = useMemo(() => {
    let out = items;
    const q = searchTerm.trim().toLowerCase();
    if (q) {
      out = out.filter((it) =>
        (it.nama_project || "").toLowerCase().includes(q) ||
        (it.domain || "").toLowerCase().includes(q)
      );
    } else if (filterPeriode.length) {
      const set = new Set(filterPeriode);
      out = out.filter((it) => set.has(it.due_date));
    }
    if (filterPayment) out = out.filter((it) => (it.payment_status || "Pending") === filterPayment);
    if (filterListing) out = out.filter((it) => (it.listing_status || "Belum Listing") === filterListing);
    return out;
  }, [items, filterPeriode, filterPayment, filterListing, searchTerm]);

  const filteredItems = useMemo(
    () => (filterClient ? itemsExceptClient.filter((it) => it.client_id === filterClient) : itemsExceptClient),
    [itemsExceptClient, filterClient]
  );

  const clientCounts = useMemo(() => {
    const m = new Map();
    for (const it of itemsExceptClient) m.set(it.client_id, (m.get(it.client_id) || 0) + 1);
    return m;
  }, [itemsExceptClient]);

  const lunasCount = useMemo(() => filteredItems.filter((i) => i.payment_status === "Lunas").length, [filteredItems]);
  const pendingCount = filteredItems.length - lunasCount;

  // Stat cards mengikuti filter tabel (periode/search/client/payment/listing), bukan total
  // seluruh group. Rumus sama dengan client_summary di backend: tagihan = amount_due (net
  // rating + tip), outstanding = tagihan - terbayar.
  const filteredStats = useMemo(() => {
    let total = 0, paid = 0;
    for (const it of filteredItems) {
      const due = parseFloat(it.amount_due ?? it.total_payment ?? 0) || 0;
      total += due;
      paid += parseFloat(it.amount_paid || 0) || 0;
    }
    return { count: filteredItems.length, total, paid, outstanding: total - paid };
  }, [filteredItems]);

  if (loading && !me) return <Loading message="Memuat data client..." />;

  return (
    <div style={{ display: "grid", gap: 12 }}>
      <Window
        title={`Selamat Datang, ${me?.client?.nama || "Client"}`}
        actions={
          <div style={{ display: "flex", gap: 6 }}>
            <button
              className="vb-btn"
              style={{ padding: "3px 10px", display: "flex", alignItems: "center", gap: 4 }}
              onClick={() => setShowRekap((v) => !v)}
              data-testid="cli-toggle-rekap"
              title={showRekap ? "Sembunyikan Rekap Pembayaran per Periode" : "Tampilkan Rekap Pembayaran per Periode"}
            >
              {showRekap ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
              {showRekap ? "Hide Rekap" : "View Rekap"}
            </button>
            <button
              className="vb-btn"
              style={{ padding: "3px 10px", display: "flex", alignItems: "center", gap: 4 }}
              onClick={() => setShowPaymentDialog(true)}
              data-testid="cli-open-payment-info"
            >
              <Wallet className="w-3 h-3" />Alamat Pembayaran
            </button>
          </div>
        }
      >
        <div style={{ fontSize: 13, color: "#003399", marginBottom: 6 }}>
          <strong>{me?.client?.nama}</strong>{" "}
          {me?.mode === "group" ? (
            <span className="vb-badge" style={{ background: "#7c3aed", color: "white", border: "none", fontSize: 9, fontWeight: 700 }}>GROUP CLIENT</span>
          ) : (
            me?.client?.group_nama && me.client.group_nama !== "-" && <span style={{ color: "#666" }}>({me.client.group_nama})</span>
          )}
        </div>
        <div style={{ fontSize: 11, color: "#666" }}>
          {me?.mode === "group"
            ? `${me?.client?.member_count || 0} client dalam group ini${me?.client?.deskripsi ? ` · ${me.client.deskripsi}` : ""}`
            : <>{me?.client?.email} {me?.client?.kontak && `· ${me.client.kontak}`} {me?.client?.alamat && `· ${me.client.alamat}`}</>}
        </div>
        <div style={{ background: "#FFFFE1", border: "1px solid #828790", padding: 8, fontSize: 12, marginTop: 8 }}>
          {me?.mode === "group"
            ? "Halaman ini menampilkan laporan SELURUH project website milik client-client dalam group Anda, beserta status pembayaran per periode (jatuh tempo tgl 15 / 30 setiap bulan)."
            : "Halaman ini menampilkan seluruh project website yang Anda pesan beserta status pembayaran per periode (jatuh tempo tgl 15 / 30 setiap bulan)."}
        </div>
      </Window>

      {/* Summary cards */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(150px, 1fr))", gap: 8 }}>
        <StatCard label="Total Project" value={filteredStats.count} testId="cli-stat-count" />
        <StatCard label="Total Tagihan" value={usd(filteredStats.total)} testId="cli-stat-total" />
        <StatCard label="Sudah Dibayar" value={usd(filteredStats.paid)} testId="cli-stat-paid" />
        <StatCard label="Belum Dibayar" value={usd(filteredStats.outstanding)} testId="cli-stat-outstanding" valueColor={filteredStats.outstanding > 0.01 ? "#d8000c" : undefined} />
        <StatCard label="Lunas / Pending" value={`${lunasCount} / ${pendingCount}`} testId="cli-stat-status" />
      </div>

      {/* Informasi Pembayaran (Bank & Crypto) SENGAJA tidak lagi ditampilkan inline di sini —
          sekarang hanya diakses lewat tombol "Alamat Pembayaran" di card paling atas (Dialog
          showPaymentDialog, dirender di bawah), per permintaan langsung user. */}

      {/* Rekap per periode */}
      {showRekap && (() => {
        // Sort newest first (descending periode date string YYYY-MM-DD)
        const allPeriodes = [...(summary?.periodes || [])].sort((a, b) => (b.periode || "").localeCompare(a.periode || ""));
        const totalPages = Math.max(1, Math.ceil(allPeriodes.length / PERIODE_PAGE_SIZE));
        const safePage = Math.min(Math.max(1, periodePage), totalPages);
        const startIdx = (safePage - 1) * PERIODE_PAGE_SIZE;
        const pagePeriodes = allPeriodes.slice(startIdx, startIdx + PERIODE_PAGE_SIZE);
        return (
          <Window title="Rekap Pembayaran per Periode (Due Date 15 / 30) — terbaru di atas">
            {allPeriodes.length === 0 ? <EmptyState message="Belum ada periode pembayaran" /> : (
              <>
                <div style={{ overflowX: "auto", WebkitOverflowScrolling: "touch" }}>
                <table className="vb-table" data-testid="cli-periode-table">
                  <thead>
                    <tr>
                      <th style={{ width: 36, textAlign: "center" }}>#</th>
                      <th><Calendar className="w-3 h-3 inline mr-1" />Periode Due Date</th>
                      <th style={{ textAlign: "center" }}>Jumlah Project</th>
                      <th style={{ textAlign: "right" }}>Total Tagihan</th>
                      <th style={{ textAlign: "right" }}>Terbayar</th>
                      <th style={{ textAlign: "right" }}>Outstanding</th>
                      <th>Status</th>
                      <th>Aksi</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pagePeriodes.map((p, i) => (
                      <tr key={p.periode}>
                        <td style={{ textAlign: "center", color: "#666", fontSize: 11 }}>{startIdx + i + 1}</td>
                        <td style={{ fontWeight: 700, color: "#003399" }}>{formatPeriode(p.periode)} <span style={{ fontSize: 10, color: "#666", fontFamily: "Consolas, monospace" }}>({p.periode})</span></td>
                        <td style={{ textAlign: "center" }}>{p.count} <span style={{ fontSize: 10, color: "#666" }}>({p.count_lunas} lunas)</span></td>
                        <td style={{ textAlign: "right" }}>
                          {p.total_gross != null && Math.abs(p.total_gross - p.total) > 0.01 ? (
                            <span><span style={{ textDecoration: "line-through", color: "#999", fontSize: 10 }}>{usd(p.total_gross)}</span> {usd(p.total)}</span>
                          ) : usd(p.total)}
                        </td>
                        <td style={{ textAlign: "right", color: "#4f8a10" }}>{usd(p.paid)}</td>
                        <td style={{ textAlign: "right", color: p.outstanding > 0.01 ? "#d8000c" : "#4f8a10", fontWeight: 600 }}>{usd(p.outstanding)}</td>
                        <td>
                          {p.outstanding <= 0.01 ? (
                            <span className="vb-badge vb-badge-approved"><CheckCircle2 className="w-3 h-3 inline mr-1" />Lunas</span>
                          ) : (
                            <span className="vb-badge vb-badge-pending"><AlertCircle className="w-3 h-3 inline mr-1" />Pending</span>
                          )}
                        </td>
                        <td>
                          <button className="vb-btn" style={{ padding: "2px 6px" }} onClick={() => setFilterPeriode([p.periode])} data-testid={`cli-filter-${p.periode}`}>
                            Filter
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                </div>
                {totalPages > 1 && (
                  <Paginator currentPage={safePage} totalPages={totalPages} onChange={setPeriodePage}
                    totalItems={allPeriodes.length} startIdx={startIdx} endIdx={Math.min(startIdx + PERIODE_PAGE_SIZE, allPeriodes.length)} />
                )}
              </>
            )}
          </Window>
        );
      })()}

      {/* List Project */}
      <Window
        title=""
        actions={
          <>
            <div style={{ position: "relative" }}>
              <Search className="w-3 h-3" style={{ position: "absolute", left: 8, top: "50%", transform: "translateY(-50%)", color: "#888" }} />
              <input
                className="vb-input"
                style={{ width: 220, paddingLeft: 24 }}
                placeholder="Cari nama project / domain..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                data-testid="cli-search"
                title="Cari di SELURUH periode, mengabaikan filter Periode"
              />
            </div>
            <select className="vb-select" style={{ width: 180 }} value={filterListing} onChange={(e) => setFilterListing(e.target.value)} data-testid="cli-filter-listing" title="Filter Status Listing">
              <option value="">Semua Status Listing</option>
              <option value="Sudah Listing">Sudah Listing</option>
              <option value="Belum Listing">Belum Listing</option>
            </select>
            {me?.mode === "group" && clientOptions.length > 1 && (
              <select className="vb-select" style={{ width: 200 }} value={filterClient} onChange={(e) => setFilterClient(e.target.value)} data-testid="cli-filter-client" title="Filter by Client">
                <option value="">Semua Client ({itemsExceptClient.length})</option>
                {clientOptions.map((c) => <option key={c.id} value={c.id}>{c.nama} ({clientCounts.get(c.id) || 0})</option>)}
              </select>
            )}
            <PeriodeMultiSelect
              options={periodeOptions}
              value={filterPeriode}
              onChange={setFilterPeriode}
              disabled={searchActive}
              title={searchActive ? "Nonaktif selama pencarian aktif — pencarian selalu mencakup semua periode" : "Centang satu atau lebih periode"}
            />
            {(filterPeriode.length || filterClient || filterListing || searchActive) && (
              <button className="vb-btn" onClick={() => { setFilterPeriode([]); setFilterClient(""); setFilterListing(""); setSearchTerm(""); }} data-testid="cli-clear-filter">Clear</button>
            )}
          </>
        }
      >
        {filteredItems.length === 0 ? <EmptyState message={(filterPeriode.length || filterClient || filterListing || searchActive) ? "Tidak ada project yang cocok dengan filter" : "Belum ada project untuk Anda"} /> : (
          <div style={{ overflowX: "auto", WebkitOverflowScrolling: "touch" }}>
            <table className="vb-table">
              <thead>
                <tr>
                  <th>Nama Project</th>
                  <th>Take By</th>
                  <th><Globe className="w-3 h-3 inline mr-1" />Domain</th>
                  <th>Type</th>
                  <th>Type Order</th>
                  <th style={{ textAlign: "right" }}>Domain</th>
                  <th style={{ textAlign: "right" }}>Web</th>
                  <th style={{ textAlign: "right" }}>Tambahan</th>
                  <th style={{ textAlign: "right" }}><DollarSign className="w-3 h-3 inline" />Total</th>
                  <th style={{ textAlign: "center" }}><Star className="w-3 h-3 inline" />Rating</th>
                  <th style={{ textAlign: "right" }}>Terbayar</th>
                  <th>Due Date</th>
                  <th>Petugas Listing</th>
                  <th>Listing</th>
                  <th>Status</th>
                  <th>Aksi</th>
                </tr>
              </thead>
              <tbody>
                {filteredItems.map((it) => {
                  const isListing = it.listing_status === "Sudah Listing";
                  const rowBg = isListing ? "#d6f3c1" : "#fff7b3";
                  return (
                  <tr key={it.id} style={{ background: rowBg }} data-testid={`cli-row-${it.id}`}>
                    <td><strong>{it.nama_project}</strong></td>
                    <td style={{ fontSize: 11 }}>{it.client_nama || "-"}</td>
                    <td>
                      <a href={"https://" + it.domain} target="_blank" rel="noreferrer" style={{ color: "#003399", textDecoration: "underline" }}>
                        {it.domain} <ExternalLink className="w-3 h-3 inline" />
                      </a>
                      {it.link_twitter && (
                        <a href={twitterHref(it.link_twitter)} target="_blank" rel="noreferrer" style={{ marginLeft: 6, color: "#1DA1F2", verticalAlign: "middle" }} title={`Twitter: ${it.link_twitter}`}>
                          <Twitter className="w-3 h-3 inline" />
                        </a>
                      )}
                    </td>
                    <td><span className={`vb-badge ${isProperType(it.type_web) ? "vb-badge-rejected" : "vb-badge-izin"}`}>{it.type_web}</span></td>
                    <td style={{ fontSize: 11 }}>{TYPE_ORDER_LABEL[it.type_order] || it.type_order || "Website"}</td>
                    <td style={{ textAlign: "right" }}>
                      {it.free_domain ? (
                        <span><span style={{ textDecoration: "line-through", color: "#999" }}>{usd(0)}</span> <span className="vb-badge vb-badge-approved" style={{ fontSize: 9 }}>FREE</span></span>
                      ) : usd(it.price_domain)}
                    </td>
                    <td style={{ textAlign: "right" }}>{usd(it.price_web)}</td>
                    <td style={{ textAlign: "right" }}>
                      {usd(parseFloat(it.tambahan || 0) + parseFloat(it.tambahan_total_to_project || 0))}
                      {(it.tambahan_breakdown || []).length > 0 && (
                        <div style={{ fontSize: 10, color: "#666", fontStyle: "italic" }}>
                          {(it.tambahan_breakdown || []).map((b) => b.nama).join(", ")}
                        </div>
                      )}
                    </td>
                    <td style={{ textAlign: "right", fontWeight: 700, color: "#003399" }}>
                      {((it.pay_percent ?? 100) < 100 || parseFloat(it.tip_amount || 0) > 0) ? (
                        <span>
                          <span style={{ textDecoration: "line-through", color: "#999", fontWeight: 400, fontSize: 11 }}>{usd(it.total_payment)}</span>
                          <br />{usd(it.amount_due)}
                          {(it.pay_percent ?? 100) < 100 && <span style={{ fontSize: 10, color: "#7c3aed" }}> ({it.pay_percent}%)</span>}
                        </span>
                      ) : usd(it.total_payment)}
                      {parseFloat(it.tip_amount || 0) > 0 && (
                        <div style={{ fontSize: 9, color: "#7c3aed", fontWeight: 400 }}>+tip {usd(it.tip_amount)}</div>
                      )}
                    </td>
                    <td style={{ textAlign: "center" }}>
                      <RatingStars value={it.rating_effective ?? 5} editable={me?.mode === "group"} onRate={(s) => setRating(it.id, s)} />
                      {(it.pay_percent ?? 100) < 100 ? (
                        <div style={{ fontSize: 10, color: "#7c3aed", fontWeight: 700 }}>−{100 - it.pay_percent}%</div>
                      ) : !it.client_rating && (
                        <div style={{ fontSize: 9, color: "#999" }}>default</div>
                      )}
                    </td>
                    <td style={{ textAlign: "right", color: "#4f8a10" }}>{usd(it.amount_paid)}</td>
                    <td style={{ fontSize: 11, color: "#d8000c" }}>{formatPeriode(it.due_date)}</td>
                    <td style={{ fontSize: 11 }}>
                      {it.listing_nama && it.listing_nama !== "-" ? it.listing_nama : <span style={{ color: "#999" }}>—</span>}
                      {it.listing_marked_by_group && (
                        <div style={{ fontSize: 9, color: "#7c3aed", fontStyle: "italic" }}>oleh Kepala Group</div>
                      )}
                    </td>
                    <td>
                      {it.revision_pending ? (
                        <span className="vb-badge" style={{ background: "#9ca3af", color: "white", border: "1px solid #6b7280", fontSize: 10, fontWeight: 700 }} title="Menunggu review admin" data-testid={`cli-listing-${it.id}`}>
                          🔄 Pending Rev
                        </span>
                      ) : isListing ? (
                        <span className="vb-badge" style={{ background: "#7bc043", color: "white", border: "1px solid #4f8a10", fontSize: 10, fontWeight: 700 }} data-testid={`cli-listing-${it.id}`}>
                          <CheckCircle2 className="w-3 h-3 inline mr-1" />Sudah Listing
                        </span>
                      ) : (
                        <span className="vb-badge" style={{ background: "#e8c000", color: "#5f4300", border: "1px solid #b89a00", fontSize: 10, fontWeight: 700 }} data-testid={`cli-listing-${it.id}`}>
                          ⏳ Belum Listing
                        </span>
                      )}
                    </td>
                    <td>
                      {it.payment_status === "Lunas" ? (
                        <span className="vb-badge vb-badge-approved"><CheckCircle2 className="w-3 h-3 inline mr-1" />Lunas</span>
                      ) : it.payment_status === "Sebagian" ? (
                        <span className="vb-badge vb-badge-pending"><Receipt className="w-3 h-3 inline mr-1" />Sebagian</span>
                      ) : (
                        <span className="vb-badge vb-badge-rejected"><AlertCircle className="w-3 h-3 inline mr-1" />Pending</span>
                      )}
                    </td>
                    <td style={{ whiteSpace: "nowrap" }}>
                      <button className="vb-btn" style={{ padding: "2px 8px", marginRight: 3 }} onClick={() => setDetail(it)} data-testid={`cli-detail-${it.id}`}>
                        <Eye className="w-3 h-3 inline mr-1" />Detail
                      </button>
                      {me?.mode === "group" && (
                        <>
                          {it.listing_status !== "Sudah Listing" && (
                            <button
                              className="vb-btn"
                              style={{ padding: "2px 8px", marginRight: 3 }}
                              title='Tandai Sudah Listing tanpa Petugas Listing — otomatis tercatat "Only Farming", tanpa biaya listing'
                              onClick={() => markListed(it)}
                              data-testid={`cli-marklisted-${it.id}`}
                            >
                              <ClipboardCheck className="w-3 h-3 inline mr-1" />Tandai Selesai
                            </button>
                          )}
                          <button
                            className="vb-btn"
                            style={{ padding: "2px 8px", marginRight: 3, opacity: it.revision_pending ? 0.5 : 1 }}
                            disabled={!!it.revision_pending}
                            title={it.revision_pending ? "Sudah ada permintaan revisi yang menunggu review admin" : "Ajukan revisi untuk project ini"}
                            onClick={() => { setRevisiTarget(it); setRevisiNote(""); }}
                            data-testid={`cli-revisi-${it.id}`}
                          >
                            <MessageSquareWarning className="w-3 h-3 inline mr-1" />Revisi
                          </button>
                          <button
                            className="vb-btn"
                            style={{ padding: "2px 8px" }}
                            title="Beri tip tambahan (USD)"
                            onClick={() => { setTipTarget(it); setTipAmount(""); }}
                            data-testid={`cli-tip-${it.id}`}
                          >
                            <HandCoins className="w-3 h-3 inline mr-1" />Tip
                          </button>
                        </>
                      )}
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Window>

      <ProjectDetailDialog
        project={detail}
        onClose={() => setDetail(null)}
        mode={me?.mode}
        onRate={(star) => detail && setRating(detail.id, star)}
        onRevisi={() => { setRevisiTarget(detail); setRevisiNote(""); }}
        onTip={() => { setTipTarget(detail); setTipAmount(""); }}
        onMarkListed={() => detail && markListed(detail)}
      />

      {/* Modal Ajukan Revisi */}
      <Dialog
        open={!!revisiTarget}
        onClose={() => { setRevisiTarget(null); setRevisiNote(""); }}
        title={`Ajukan Revisi — ${revisiTarget?.nama_project || ""}`}
        width={480}
        footer={<>
          <button className="vb-btn" onClick={() => { setRevisiTarget(null); setRevisiNote(""); }}>Batal</button>
          <button className="vb-btn-primary" onClick={submitRevisi} data-testid="cli-revisi-submit">Kirim Revisi</button>
        </>}
      >
        <div style={{ fontSize: 12, color: "#666", marginBottom: 8 }}>
          Jelaskan bagian mana yang perlu direvisi. Status project akan berubah menjadi <strong>Pending Rev</strong> sampai admin menyetujui permintaan ini.
        </div>
        <textarea
          rows={5}
          className="vb-textarea"
          style={{ width: "100%" }}
          placeholder="Contoh: mohon ganti warna tombol utama menjadi biru..."
          value={revisiNote}
          onChange={(e) => setRevisiNote(e.target.value)}
          data-testid="cli-revisi-note"
        />
      </Dialog>

      {/* Modal Beri Tip */}
      <Dialog
        open={!!tipTarget}
        onClose={() => { setTipTarget(null); setTipAmount(""); }}
        title={`Beri Tip — ${tipTarget?.nama_project || ""}`}
        width={380}
        footer={<>
          <button className="vb-btn" onClick={() => { setTipTarget(null); setTipAmount(""); }}>Batal</button>
          <button className="vb-btn-primary" onClick={submitTip} data-testid="cli-tip-submit">Kirim Tip</button>
        </>}
      >
        <div style={{ fontSize: 12, color: "#666", marginBottom: 8 }}>
          Nominal tip akan langsung ditambahkan ke total tagihan project ini.
          {parseFloat(tipTarget?.tip_amount || 0) > 0 && <> Tip yang sudah diberikan sebelumnya: <strong>{usd(tipTarget.tip_amount)}</strong>.</>}
        </div>
        <div className="vb-form-row">
          <label className="vb-label">Nominal Tip (USD)*</label>
          <input
            type="number" step="0.01" min="0.01"
            className="vb-input"
            value={tipAmount}
            onChange={(e) => setTipAmount(e.target.value)}
            placeholder="0.00"
            data-testid="cli-tip-amount"
          />
        </div>
      </Dialog>

      {/* Dialog Alamat Pembayaran — quick-access dari card paling atas */}
      <Dialog
        open={showPaymentDialog}
        onClose={() => setShowPaymentDialog(false)}
        title={<><Wallet className="w-4 h-4 inline mr-1" />Alamat Pembayaran</>}
        width={720}
        footer={<button className="vb-btn" onClick={() => setShowPaymentDialog(false)} data-testid="cli-payment-info-close">Tutup</button>}
      >
        {paymentInfo.length === 0 ? (
          <EmptyState message="Admin belum mengatur rekening / wallet pembayaran." />
        ) : (
          <PaymentInfoGrid paymentInfo={paymentInfo} />
        )}
      </Dialog>
    </div>
  );
}

// ============ Payment Info Grid: daftar rekening/wallet + tombol copy ============
// Dipakai bersama oleh Window "Informasi Pembayaran" (inline, di bawah summary cards) DAN
// Dialog "Alamat Pembayaran" (quick-access dari tombol di card paling atas) — satu sumber
// render, supaya keduanya tidak bisa diam-diam beda tampilan.
export function PaymentInfoGrid({ paymentInfo }) {
  // QR Code popup — cuma untuk wallet crypto (p.type !== "bank"), menampilkan QR dari alamat
  // wallet-nya sendiri (bukan sekadar teks alamat) supaya bisa langsung di-scan dari aplikasi
  // wallet. Reuse `qrcode.react` yang sudah jadi dependency (dipakai `employee/Koperasi.jsx`),
  // tidak perlu library baru.
  const [qrTarget, setQrTarget] = useState(null);
  return (
    <>
      <div style={{ fontSize: 12, padding: 8, background: "#fffbe6", border: "1px solid #d4a017", marginBottom: 10 }}>
        💡 Silakan transfer ke salah satu rekening / wallet di bawah. Konfirmasi pembayaran ke admin via WA setelah transfer.
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 10 }}>
        {paymentInfo.map((p) => (
          <div key={p.id} className="vb-window" style={{ margin: 0 }} data-testid={`cli-payinfo-${p.id}`}>
            <div className="vb-titlebar">
              <span>
                {p.type === "bank" ? <Landmark className="w-3 h-3 inline mr-1" /> : <Bitcoin className="w-3 h-3 inline mr-1" />}
                {p.label}
              </span>
              <span className="vb-badge" style={{ background: p.type === "bank" ? "#1a73e8" : "#f7931a", color: "white", border: "none", fontSize: 9, fontWeight: 700 }}>
                {p.type === "bank" ? "BANK" : "CRYPTO"} · {p.currency}
              </span>
            </div>
            <div className="vb-content" style={{ padding: 10, fontSize: 12 }}>
              {p.type === "bank" ? (
                <>
                  <div><strong>Bank:</strong> {p.bank_nama}</div>
                  <div style={{ marginTop: 4 }}>
                    <strong>No. Rekening:</strong>
                    <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 2 }}>
                      <code style={{ fontSize: 14, fontWeight: 700, fontFamily: "Consolas, monospace", background: "#f5f5f5", padding: "4px 8px", flex: 1 }}>{p.no_rekening}</code>
                      <button className="vb-btn" style={{ padding: "3px 6px" }} title="Copy" onClick={() => { navigator.clipboard.writeText(p.no_rekening); toast.success("No rekening tersalin"); }}><Copy className="w-3 h-3" /></button>
                    </div>
                  </div>
                  <div style={{ marginTop: 4 }}><strong>Atas Nama:</strong> {p.atas_nama}</div>
                  {p.swift_code && <div style={{ marginTop: 4, fontSize: 11 }}><strong>SWIFT:</strong> <code>{p.swift_code}</code></div>}
                </>
              ) : (
                <>
                  <div><strong>Network:</strong> <span className="vb-badge vb-badge-approved" style={{ fontSize: 10, padding: "1px 5px" }}>{p.network}</span></div>
                  <div style={{ marginTop: 4 }}>
                    <strong>Address:</strong>
                    <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 2 }}>
                      <code style={{ fontSize: 11, fontFamily: "Consolas, monospace", background: "#f5f5f5", padding: "4px 8px", flex: 1, wordBreak: "break-all" }}>{p.wallet_address}</code>
                      <button className="vb-btn" style={{ padding: "3px 6px" }} title="Copy" onClick={() => { navigator.clipboard.writeText(p.wallet_address); toast.success("Alamat tersalin"); }}><Copy className="w-3 h-3" /></button>
                      <button className="vb-btn" style={{ padding: "3px 6px" }} title="Tampilkan QR Code" onClick={() => setQrTarget(p)} data-testid={`cli-payinfo-qr-${p.id}`}><QrCode className="w-3 h-3" /></button>
                    </div>
                  </div>
                  {p.memo_tag && <div style={{ marginTop: 4, fontSize: 11 }}><strong>Memo / Tag:</strong> <code>{p.memo_tag}</code> <span style={{ color: "#d8000c" }}>⚠ wajib</span></div>}
                </>
              )}
              {p.instruksi && (
                <div style={{ marginTop: 8, padding: 6, background: "#fff8d7", border: "1px solid #d6b656", fontSize: 11, color: "#5f4300" }}>
                  ⚠ {p.instruksi}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Popup QR Code — wallet crypto saja */}
      <Dialog
        open={!!qrTarget}
        onClose={() => setQrTarget(null)}
        title={`QR Code — ${qrTarget?.label || ""}`}
        width={320}
        footer={<button className="vb-btn" onClick={() => setQrTarget(null)} data-testid="cli-payinfo-qr-close">Tutup</button>}
      >
        {qrTarget && (
          <div style={{ textAlign: "center" }}>
            <div style={{ display: "inline-block", background: "white", padding: 10, border: "1px solid #ccc" }}>
              <QRCodeCanvas value={qrTarget.wallet_address || ""} size={200} level="H" includeMargin={false} data-testid="cli-payinfo-qr-canvas" />
            </div>
            <div style={{ marginTop: 8, fontSize: 11, color: "#666" }}>
              <span className="vb-badge vb-badge-approved" style={{ fontSize: 10, padding: "1px 5px" }}>{qrTarget.network}</span>
            </div>
            <code style={{ display: "block", marginTop: 6, fontSize: 10, fontFamily: "Consolas, monospace", wordBreak: "break-all", color: "#555" }}>
              {qrTarget.wallet_address}
            </code>
            <button
              className="vb-btn"
              style={{ marginTop: 8 }}
              onClick={() => { navigator.clipboard.writeText(qrTarget.wallet_address); toast.success("Alamat tersalin"); }}
              data-testid="cli-payinfo-qr-copy"
            >
              <Copy className="w-3 h-3 inline mr-1" />Copy Alamat
            </button>
          </div>
        )}
      </Dialog>
    </>
  );
}

// ============ Project Detail Dialog ============
function ProjectDetailDialog({ project, onClose, mode, onRate, onRevisi, onTip, onMarkListed }) {
  if (!project) return null;
  const payPct = project.pay_percent ?? 100;
  const grossTotal = parseFloat(project.total_payment || 0);
  const tipAmt = parseFloat(project.tip_amount || 0);
  const amountDue = parseFloat(project.amount_due ?? (grossTotal + tipAmt) ?? 0);
  // Potongan rating dihitung dari bagian sebelum tip (tip tidak ikut dipotong rating).
  const ratingDiscount = grossTotal - (amountDue - tipAmt);
  const outstanding = amountDue - parseFloat(project.amount_paid || 0);
  const subtotalTambahan = parseFloat(project.tambahan || 0) + parseFloat(project.tambahan_total_to_project || 0);
  return (
    <Dialog open={!!project} onClose={onClose} title={`Detail Project — ${project.nama_project}`} width={620}
      footer={<button className="vb-btn" onClick={onClose} data-testid="cli-detail-close">Tutup</button>}
    >
      {/* Project info */}
      <div style={{ background: "#FFFFE1", border: "1px solid #828790", padding: 10, marginBottom: 10 }}>
        <div style={{ fontSize: 14, fontWeight: 700, color: "#003399", marginBottom: 4 }}>{project.nama_project}</div>
        <div style={{ fontSize: 12 }}>
          <strong>Domain:</strong>{" "}
          <a href={"https://" + project.domain} target="_blank" rel="noreferrer" style={{ color: "#003399", textDecoration: "underline" }}>
            {project.domain} <ExternalLink className="w-3 h-3 inline" />
          </a>
        </div>
        <div style={{ fontSize: 12 }}>
          <strong>Type Web:</strong> {project.type_web} · <strong>Ext:</strong> .{project.ext} · <strong>Type Order:</strong> {TYPE_ORDER_LABEL[project.type_order] || project.type_order || "Website"}
        </div>
        <div style={{ fontSize: 12 }}>
          <strong>Take By:</strong> {project.client_nama || "-"} · <strong>Petugas Listing:</strong> {project.listing_nama && project.listing_nama !== "-" ? project.listing_nama : "—"}
          {project.listing_marked_by_group && <span style={{ color: "#7c3aed", fontStyle: "italic", fontSize: 11 }}> (oleh Kepala Group)</span>}
        </div>
        <div style={{ fontSize: 12, marginTop: 4, display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
          <strong>Rating:</strong>
          <RatingStars value={project.rating_effective ?? 5} editable={mode === "group" && !!onRate} onRate={(s) => onRate(s)} size={17} />
          {payPct < 100 ? (
            <span style={{ color: "#7c3aed", fontWeight: 700 }}>−{100 - payPct}% → cukup bayar {payPct}%</span>
          ) : !project.client_rating ? (
            <span style={{ color: "#666", fontSize: 11 }}>
              default 5★{mode === "group" ? " — beri rating lebih rendah untuk potongan harga (tiap bintang di bawah 5 = −10%, min 60%)" : ""}
            </span>
          ) : null}
        </div>
        {mode === "group" && (onRevisi || onTip || onMarkListed) && (
          <div style={{ marginTop: 8, display: "flex", gap: 6, flexWrap: "wrap" }}>
            {onMarkListed && project.listing_status !== "Sudah Listing" && (
              <button className="vb-btn" onClick={onMarkListed} data-testid="cli-detail-marklisted">
                <ClipboardCheck className="w-3 h-3 inline mr-1" />Tandai Selesai (Only Farming)
              </button>
            )}
            {onRevisi && (
              <button
                className="vb-btn"
                style={{ opacity: project.revision_pending ? 0.5 : 1 }}
                disabled={!!project.revision_pending}
                title={project.revision_pending ? "Sudah ada permintaan revisi yang menunggu review admin" : "Ajukan revisi untuk project ini"}
                onClick={onRevisi}
                data-testid="cli-detail-revisi"
              >
                <MessageSquareWarning className="w-3 h-3 inline mr-1" />Ajukan Revisi
              </button>
            )}
            {onTip && (
              <button className="vb-btn" onClick={onTip} data-testid="cli-detail-tip">
                <HandCoins className="w-3 h-3 inline mr-1" />Beri Tip
              </button>
            )}
          </div>
        )}
        {project.link_twitter && (
          <div style={{ fontSize: 12 }}>
            <strong>Twitter:</strong>{" "}
            <a href={twitterHref(project.link_twitter)} target="_blank" rel="noreferrer" style={{ color: "#1DA1F2", textDecoration: "underline" }}>
              {project.link_twitter} <ExternalLink className="w-3 h-3 inline" />
            </a>
          </div>
        )}
        <div style={{ fontSize: 12 }}>
          <strong>Tanggal Buat:</strong> {project.tanggal_pembuatan} · <strong>Due Date:</strong> <span style={{ color: "#d8000c" }}>{formatPeriode(project.due_date)}</span>
        </div>
        <div style={{ fontSize: 12, marginTop: 4 }}>
          <strong>Status Listing:</strong>{" "}
          {project.revision_pending ? (
            <span className="vb-badge" style={{ background: "#9ca3af", color: "white", border: "1px solid #6b7280", fontSize: 10, fontWeight: 700 }}>
              🔄 Pending Rev
            </span>
          ) : project.listing_status === "Sudah Listing" ? (
            <span className="vb-badge" style={{ background: "#7bc043", color: "white", border: "1px solid #4f8a10", fontSize: 10, fontWeight: 700 }}>
              <CheckCircle2 className="w-3 h-3 inline mr-1" />Sudah Listing
            </span>
          ) : (
            <span className="vb-badge" style={{ background: "#e8c000", color: "#5f4300", border: "1px solid #b89a00", fontSize: 10, fontWeight: 700 }}>
              ⏳ Belum Listing
            </span>
          )}
        </div>
        {project.revision_pending && project.revision_note && (
          <div style={{ marginTop: 6, padding: 8, background: "#f3f4f6", border: "1px solid #9ca3af", fontSize: 11 }}>
            <strong>Catatan revisi Anda:</strong> {project.revision_note}
          </div>
        )}
        {project.catatan && (
          <div style={{ fontSize: 11, color: "#666", marginTop: 4, fontStyle: "italic" }}>&ldquo;{project.catatan}&rdquo;</div>
        )}
      </div>

      {/* Rincian Biaya */}
      <h3 className="vb-h1" style={{ marginBottom: 6 }}>Rincian Biaya</h3>
      <table className="vb-table" data-testid="cli-detail-breakdown">
        <thead>
          <tr><th>Komponen</th><th>Keterangan</th><th style={{ textAlign: "right" }}>Nominal (USD)</th></tr>
        </thead>
        <tbody>
          <tr>
            <td>Domain</td>
            <td style={{ fontSize: 11 }}>
              {project.domain} (ext .{project.ext})
              {project.free_domain && <span className="vb-badge vb-badge-approved" style={{ marginLeft: 6, fontSize: 9 }}>FREE</span>}
            </td>
            <td style={{ textAlign: "right" }}>
              {project.free_domain ? (
                <span><span style={{ textDecoration: "line-through", color: "#999" }}>{usd(0)}</span> {usd(0)}</span>
              ) : usd(project.price_domain)}
            </td>
          </tr>
          <tr>
            <td>Website</td>
            <td style={{ fontSize: 11 }}>
              {project.type_web}
              {project.type_order === "REBRAND" && <span style={{ color: "#7c3aed", fontStyle: "italic" }}> · Rebrand (50% dari harga Type Web)</span>}
              {project.type_order === "MATERIAL_ONLY" && <span style={{ color: "#6b7280", fontStyle: "italic" }}> · Material Only (harga flat)</span>}
              {project.type_order === "LISTING_ONLY" && <span style={{ color: "#0891b2", fontStyle: "italic" }}> · Listing Only (Domain & Web sudah lunas periode sebelumnya)</span>}
            </td>
            <td style={{ textAlign: "right" }}>{usd(project.price_web)}</td>
          </tr>

          {/* Biaya Listing (Rate Listing) — hanya kalau ada petugas listing & rate-nya > 0 */}
          {parseFloat(project.price_listing || 0) > 0 && (
            <tr>
              <td>Biaya Listing</td>
              <td style={{ fontSize: 11 }}>
                Rate Harga Listing
                {project.listing_nama && project.listing_nama !== "-" && <span style={{ color: "#666" }}> · Petugas: {project.listing_nama}</span>}
                {project.listing_status !== "Sudah Listing" && <span style={{ color: "#b8860b" }}> · Belum ditagih (menunggu listing selesai)</span>}
              </td>
              <td style={{ textAlign: "right" }}>{usd(project.price_listing)}</td>
            </tr>
          )}

          {/* Tip Tambahan (dari Group Client) — dibayar penuh, tidak ikut dipotong rating */}
          {tipAmt > 0 && (
            <tr>
              <td>Tip Tambahan</td>
              <td style={{ fontSize: 11, color: "#7c3aed" }}>Tip dari Group Client (tidak ikut dipotong rating)</td>
              <td style={{ textAlign: "right", color: "#7c3aed", fontWeight: 700 }}>{usd(tipAmt)}</td>
            </tr>
          )}

          {/* Tambahan Manual (legacy) */}
          {parseFloat(project.tambahan || 0) > 0 && (
            <tr>
              <td>Tambahan Manual</td>
              <td style={{ fontSize: 11, fontStyle: "italic" }}>{project.tambahan_keterangan || "-"}</td>
              <td style={{ textAlign: "right" }}>{usd(project.tambahan)}</td>
            </tr>
          )}

          {/* Tambahan Terdaftar (bluetick-like) */}
          {(project.tambahan_breakdown || []).map((b, idx) => (
            <tr key={idx}>
              <td>Tambahan</td>
              <td style={{ fontSize: 11 }}>
                <strong>{b.nama}</strong>
                {b.currency && b.currency !== "USD" && (
                  <span style={{ color: "#666", fontSize: 10 }}> (asli {b.currency} {parseFloat(b.nominal || 0).toLocaleString()})</span>
                )}
              </td>
              <td style={{ textAlign: "right" }}>
                {b.currency === "USD" ? usd(b.nominal) : usd(b.nominal_usd || b.nominal)}
              </td>
            </tr>
          ))}

          {(project.tambahan_breakdown || []).length === 0 && parseFloat(project.tambahan || 0) === 0 && (
            <tr>
              <td colSpan={3} style={{ fontStyle: "italic", color: "#666", fontSize: 11, textAlign: "center" }}>
                Tidak ada biaya tambahan
              </td>
            </tr>
          )}
        </tbody>
        <tfoot>
          <tr style={{ background: "#ececec", fontWeight: 700 }}>
            <td colSpan={2} style={{ textAlign: "right" }}>{(payPct < 100 || tipAmt > 0) ? "Subtotal:" : "TOTAL TAGIHAN:"}</td>
            <td style={{ textAlign: "right", color: "#003399", fontSize: 14 }}>{usd(grossTotal + tipAmt)}</td>
          </tr>
          {payPct < 100 && (
            <tr style={{ background: "#f3e8ff", color: "#7c3aed", fontWeight: 700 }}>
              <td colSpan={2} style={{ textAlign: "right" }}>Potongan Rating ({project.client_rating}★ · −{100 - payPct}%):</td>
              <td style={{ textAlign: "right" }}>− {usd(ratingDiscount)}</td>
            </tr>
          )}
          {(payPct < 100 || tipAmt > 0) && (
            <tr style={{ background: "#dcfce7", fontWeight: 700 }}>
              <td colSpan={2} style={{ textAlign: "right" }}>TOTAL YANG PERLU DIBAYAR{payPct < 100 ? ` (${payPct}%)` : ""}:</td>
              <td style={{ textAlign: "right", color: "#166534", fontSize: 15 }}>{usd(amountDue)}</td>
            </tr>
          )}
        </tfoot>
      </table>

      {/* Status Pembayaran */}
      <h3 className="vb-h1" style={{ marginBottom: 6, marginTop: 12 }}>Status Pembayaran</h3>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8 }}>
        <div className="vb-statcard">
          <div className="vb-statcard-label">{payPct < 100 ? `Yang Perlu Dibayar (${payPct}%)` : "Total Tagihan"}</div>
          <div className="vb-statcard-value" style={{ color: "#003399" }}>{usd(amountDue)}</div>
          {(payPct < 100 || tipAmt > 0) && <div style={{ fontSize: 10, color: "#999", textDecoration: payPct < 100 ? "line-through" : "none" }}>{usd(grossTotal + tipAmt)}</div>}
        </div>
        <div className="vb-statcard">
          <div className="vb-statcard-label">Sudah Dibayar</div>
          <div className="vb-statcard-value" style={{ color: "#4f8a10" }}>{usd(project.amount_paid)}</div>
        </div>
        <div className="vb-statcard">
          <div className="vb-statcard-label">Outstanding</div>
          <div className="vb-statcard-value" style={{ color: outstanding > 0.01 ? "#d8000c" : "#4f8a10" }}>{usd(outstanding)}</div>
        </div>
      </div>

      <div style={{ marginTop: 8, padding: 8, background: "#f5f5f5", border: "1px solid #ccc", fontSize: 12 }}>
        <strong>Status:</strong>{" "}
        {project.payment_status === "Lunas" ? (
          <span className="vb-badge vb-badge-approved"><CheckCircle2 className="w-3 h-3 inline mr-1" />Lunas</span>
        ) : project.payment_status === "Sebagian" ? (
          <span className="vb-badge vb-badge-pending"><Receipt className="w-3 h-3 inline mr-1" />Sebagian Dibayar</span>
        ) : (
          <span className="vb-badge vb-badge-rejected"><AlertCircle className="w-3 h-3 inline mr-1" />Belum Dibayar</span>
        )}
        {project.last_payment_date && (
          <span style={{ marginLeft: 10, color: "#666" }}>Pembayaran terakhir: {project.last_payment_date}</span>
        )}
      </div>
    </Dialog>
  );
}

// ============ Paginator: 10 per page + numbered buttons with ellipsis ============
function Paginator({ currentPage, totalPages, onChange, totalItems, startIdx, endIdx }) {
  // Build page numbers with ellipsis (max ~7 visible buttons)
  const pages = [];
  const push = (v) => pages.push(v);
  if (totalPages <= 7) {
    for (let i = 1; i <= totalPages; i++) push(i);
  } else {
    push(1);
    if (currentPage > 4) push("...");
    const start = Math.max(2, currentPage - 1);
    const end = Math.min(totalPages - 1, currentPage + 1);
    for (let i = start; i <= end; i++) push(i);
    if (currentPage < totalPages - 3) push("...");
    push(totalPages);
  }

  return (
    <div style={{ marginTop: 10, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, flexWrap: "wrap", fontSize: 12 }} data-testid="cli-periode-paginator">
      <div style={{ color: "#666" }}>
        Menampilkan <strong>{startIdx + 1}–{endIdx}</strong> dari <strong>{totalItems}</strong> periode
      </div>
      <div style={{ display: "flex", gap: 4 }}>
        <button className="vb-btn" disabled={currentPage <= 1} onClick={() => onChange(currentPage - 1)} data-testid="cli-page-prev">‹ Prev</button>
        {pages.map((p, i) => (
          p === "..." ? (
            <span key={`e-${i}`} style={{ padding: "4px 6px", color: "#666" }}>...</span>
          ) : (
            <button
              key={p}
              className={p === currentPage ? "vb-btn-primary" : "vb-btn"}
              style={{ padding: "2px 9px", minWidth: 28, fontWeight: p === currentPage ? 700 : 400 }}
              onClick={() => onChange(p)}
              data-testid={`cli-page-${p}`}
            >
              {p}
            </button>
          )
        ))}
        <button className="vb-btn" disabled={currentPage >= totalPages} onClick={() => onChange(currentPage + 1)} data-testid="cli-page-next">Next ›</button>
      </div>
    </div>
  );
}

